import { Cluster, Renderer } from "@googlemaps/markerclusterer";

export interface IClusterRenderer extends Renderer {
}

export type MarkerTypeConfig = {
  color: string;
  pulsate?: boolean;
}

export abstract class BaseClusterRenderer implements IClusterRenderer {
  // Override this to define your marker types config
  protected abstract getMarkerTypesConfig(): { [key: string]: MarkerTypeConfig };

  // Override this to extract the type from a marker
  protected abstract getMarkerType(marker: google.maps.marker.AdvancedMarkerElement): string | null;

  render(cluster: Cluster): google.maps.marker.AdvancedMarkerElement {
    const { count, position, markers } = cluster;
    const typesConfig = this.getMarkerTypesConfig();

    // Count markers by type
    const counts: { [key: string]: number } = {};
    for (const marker of markers ?? []) {
      const markerElement = (marker as google.maps.marker.AdvancedMarkerElement)
      const type = this.getMarkerType(markerElement);

      if (type && typesConfig[type]) {
        counts[type] = (counts[type] || 0) + 1;
      }
    }

    // Find dominant type and create pie background
    const total = Object.values(counts).reduce((sum, count) => sum + count, 0) || 1;
    let dominantType = Object.keys(typesConfig)[0];
    let maxCount = 0;

    for (const [type, count] of Object.entries(counts)) {
      if (count > maxCount) {
        maxCount = count;
        dominantType = type;
      }
    }

    // Create pie chart background
    const gradientParts: string[] = [];
    let currentPercent = 0;
    for (const [type, count] of Object.entries(counts)) {

      if (count > 0) {
        const percent = Math.round((count / total) * 100);
        gradientParts.push(`${typesConfig[type].color} ${currentPercent}% ${currentPercent + percent}%`);
        currentPercent += percent;
      }
    }

    // Check if ALL markers are of a pulsating type
    const shouldPulsate = typesConfig[dominantType]?.pulsate &&
      Object.keys(counts).filter(type => counts[type] > 0).length === 1 &&
      counts[dominantType] === count;

    // Create cluster element
    const div = document.createElement('div');
    div.className = `cluster ${count >= 100 ? 'cluster--l' : count >= 10 ? 'cluster--m' : 'cluster--s'} ${shouldPulsate ? 'cluster--pulsate' : ''}`;
    div.style.setProperty('--cluster-color', typesConfig[dominantType].color);
    div.style.background = `conic-gradient(${gradientParts.join(', ')})`;
    const label = document.createElement('span');
    label.className = 'cluster__count';
    label.textContent = String(count);
    div.appendChild(label);

    return new google.maps.marker.AdvancedMarkerElement({
      position,
      content: div,
      zIndex: 100000 + count
    });
  }
}